<template>
    <div class="form-container main-bg-color">
        <Toast />

        <!-- CellXpert operator handoff. :closable="false" and no dismissable
             mask on purpose - the only way out is Done, so the admin cannot
             click past it and forget to message the operator. -->
        <Dialog v-model:visible="showCellxpertHandoff" modal :closable="false"
            :dismissableMask="false" :draggable="false" header="Send this to the CellXpert operator"
            class="w-[92vw] md:w-[42rem]">
            <p class="mb-3 text-sm">
                <strong>{{ cellxpertHandoffFor }}</strong> is saved, but it will not pull any data
                until the operator whitelists our IP. Send them this message:
            </p>

            <pre class="cx-message">{{ cellxpertMessage }}</pre>

            <div class="flex items-center gap-2 mt-3 flex-wrap">
                <Button label="Copy message" icon="pi pi-copy" severity="secondary"
                    @click="copyToClipboard(cellxpertMessage)" />
                <span class="text-xs opacity-60">IP {{ CELLXPERT_PULLER_IP }} · qFTD and NGR columns</span>
            </div>

            <p class="text-xs opacity-60 mt-3">
                Both are silent failures — the login saves fine and simply returns nothing —
                so this is easy to miss until the numbers are already wrong.
            </p>

            <template #footer>
                <Button label="Done — I've sent it" icon="pi pi-check" @click="confirmCellxpertHandoff" />
            </template>
        </Dialog>
        <div class="max-w-8xl mx-auto px-6 pb-2 rounded shadow">
            <div class="  ">
                <div class="flex flex-col md:flex-row gap-4">
                    <!-- Section A -->
                    <div class="flex-1 card-bg border rounded-xl border-gray-300 px-5 py-2">
                        <h2 class="text-xl font-bold mb-4 border-b-2 border-black">Basic Information</h2>
                        <div class="md:grid grid-cols-1 md:grid-cols-2 gap-4"
                            style="grid-template-columns: 45% 5% 35%;">
                            <div class="mb-4 flex flex-col w-full">
                                <label for="name" class="block text-sm font-medium ">Voonix
                                    AdvertiserName</label>
                                <div>
                                    <InputText class="w-full mt-1" id="name" v-model="form.name"
                                        :invalid="errors.name !== null" placeholder="Name" required />
                                    <small class="error" v-if="errors.name">{{ errors.name }}</small>
                                </div>
                            </div>
                            <div />
                            <div class="mb-4">
                                <div class="flex items-center">
                                    <label for="currency" class="block text-sm font-medium  mr-2">
                                        Space Advertiser
                                    </label>
                                    <a class="text-blue-500 hover:underline cursor-pointer"
                                        @click="openCreateNewSpaceAdvertiserPrompt($event)">
                                        Create new
                                    </a>
                                    <ConfirmPopup group="headless">
                                        <template #container="{ acceptCallback, rejectCallback }">
                                            <div class="rounded p-4">
                                                <div class="mb-4 flex flex-col w-[24rem]">
                                                    <label for="name" class="block text-sm font-medium ">
                                                        Space Advertiser name</label>
                                                    <div>
                                                        <InputText v-model="newSpaceAdvertiserName"
                                                            class="w-full md:w-3/5 mt-1" id="newSpaceAdvertiserName"
                                                            :invalid="nameError !== null" placeholder="Name" required
                                                            autofocus />
                                                        <small class="error block" v-if="false">err</small>
                                                    </div>
                                                </div>
                                                <div class="flex items-center gap-2 mt-4">
                                                    <Button label="Create" @click="acceptCallback" severity="primary"
                                                        size="small"></Button>
                                                    <Button label="Cancel" outlined @click="rejectCallback"
                                                        severity="secondary" size="small" text></Button>
                                                </div>
                                            </div>
                                        </template>
                                    </ConfirmPopup>
                                </div>
                                <div>
                                    <Select id="space-advertiser-select" filter class="w-full  mt-1"
                                        v-model="form.spaceAdvertiserId" :options="spaceAdvertisers" optionLabel="name"
                                        @click="fetchSpaceAdvertisers" :loading="loadingSpaceAdvertisers"
                                        optionValue="id" aria-labelledby="basic" placeholder="Select an advertiser" />
                                    <br />
                                    <small class="error" v-if="errors.spaceAdvertiser">{{ errors.spaceAdvertiser
                                        }}</small>
                                </div>
                            </div>
                        </div>
                        <div class="md:grid grid-cols-1 md:grid-cols-2 gap-4"
                            style="grid-template-columns: 45% 5% 35%;">
                            <div class="mb-4">
                                <label for="currency" class="block text-sm font-medium ">Currency</label>
                                <div>
                                    <Select id="currency" filter class="w-full  mt-1"
                                        :invalid="errors.currency !== null" v-model="form.currency"
                                        :options="currencies" optionLabel="code" optionValue="code"
                                        aria-labelledby="basic" placeholder="Select a currency" />
                                    <br />
                                    <small class="error" v-if="errors.currency">{{ errors.currency }}</small>
                                </div>
                            </div>
                            <div />
                            <div class="mb-4 flex flex-col w-full">
                                <label for="description" class="block text-sm font-medium ">Description</label>
                                <Textarea rows="2" class="mt-1 block w-full " id="description"
                                    v-model="form.description" placeholder="Description" required />
                            </div>
                        </div>

                    </div>

                    <div class="flex-1  card-bg border rounded-xl border-gray-300 px-5 py-2">
                        <h2 class="text-xl font-bold mb-4 border-b-2 border-black">Payment Information</h2>
                        <div class="mt-3">
                            <div :class="{ 'h-15': !showPayerForm, 'h-[20rem]': showPayerForm }"
                                class="overflow-y-auto pr-4">
                                <!-- Add Payer Button -->
                                <div v-if="!showPayerForm">
                                    <Button label="Add Payer Details" icon="pi pi-plus" @click="showPayerForm = true"
                                        class="p-button-outlined" />
                                </div>

                                <!-- Payer Form -->
                                <div v-else>
                                    <div class="flex flex-col mt-3">
                                        <label for="payer-payment-method" class="block text-sm font-medium ">Payment
                                            method</label>
                                        <Select id="payer-payment-method" v-model="payerForm.paymentMethod"
                                            :options="paymentMethods" optionLabel="name" optionValue="code"
                                            aria-labelledby="basic" placeholder="Select a payment method"
                                            class="w-full mt-1 md:w-5/6" />
                                        <small class="error" v-if="paymentErrors.paymentMethod">{{
                                            paymentErrors.paymentMethod }}</small>
                                    </div>
                                    <div class="flex flex-col mt-3">
                                        <label for="payer-payment-frequency" class="block text-sm font-medium ">Payment
                                            frequency</label>
                                        <Select id="payer-payment-frequency" v-model="payerForm.paymentFrequency"
                                            :options="paymentFrequencies" optionLabel="name" optionValue="code"
                                            aria-labelledby="basic" placeholder="Select a payment frequency"
                                            class="w-full mt-1 md:w-5/6" />
                                        <small class="error" v-if="paymentErrors.paymentMethod">{{
                                            paymentErrors.paymentMethod }}</small>
                                    </div>

                                    <div class="flex flex-col mt-3">
                                        <label for="payer-threshold"
                                            class="block text-sm font-medium ">Threshold</label>
                                        <InputNumber prefix="€" id="payer-threshold" v-model="payerForm.threshold"
                                            placeholder="Threshold" class="w-full mt-1 md:w-5/6" />
                                    </div>

                                    <div class="flex flex-col mt-3">
                                        <label for="payer-note" class="block text-sm font-medium ">Paste Details here</label>
                                        <Textarea rows="2" id="payer-undsturctured-details" v-model="payerForm.unstructuredPayerDetails" placeholder="Paste here"
                                            class="w-full mt-1 md:w-5/6" />
                                    </div>

                                    <!-- <div class="flex flex-col mt-3">
                                        <label for="payer-name" class="block text-sm font-medium ">Name</label>
                                        <InputText id="payer-name" v-model="payerForm.name" placeholder="Payer Name"
                                            class="w-full mt-1 md:w-5/6" />
                                        <small class="error" v-if="paymentErrors.payerName">{{ paymentErrors.payerName
                                            }}</small>
                                    </div>

                                    <div class="flex flex-col mt-3">
                                        <label for="payer-note" class="block text-sm font-medium ">Paste Details here</label>
                                        <Textarea rows="2" id="payer-undsturctured-details" v-model="payerForm.unstructuredPayerDetails" placeholder="Paste here"
                                            class="w-full mt-1 md:w-5/6" />
                                    </div>

                                    <div class="flex flex-col mt-3">
                                        <label for="payer-country" class="block text-sm font-medium ">Country</label>
                                        <InputText id="payer-country" v-model="payerForm.country" placeholder="Country"
                                            class="w-full mt-1 md:w-5/6" />
                                        <small class="error" v-if="paymentErrors.country">{{ paymentErrors.country
                                            }}</small>
                                    </div>

                                    <div class="flex flex-col mt-3">
                                        <label for="payer-city" class="block text-sm font-medium ">City</label>
                                        <InputText id="payer-city" v-model="payerForm.city" placeholder="City"
                                            class="w-full mt-1 md:w-5/6" />
                                        <small class="error" v-if="paymentErrors.city">{{ paymentErrors.city }}</small>
                                    </div>

                                    <div class="flex flex-col mt-3">
                                        <label for="payer-address" class="block text-sm font-medium ">Address</label>
                                        <InputText id="payer-address" v-model="payerForm.address" placeholder="Address"
                                            class="w-full mt-1 md:w-5/6" />
                                        <small class="error" v-if="paymentErrors.address">{{ paymentErrors.address
                                            }}</small>
                                    </div>

                                    <div class="flex flex-col mt-3">
                                        <label for="payer-zip" class="block text-sm font-medium ">Zip</label>
                                        <InputText id="payer-zip" v-model="payerForm.zip" placeholder="Zip"
                                            class="w-full mt-1 md:w-5/6" />
                                    </div>

                                    <div class="flex flex-col mt-3">
                                        <label for="payer-vat" class="block text-sm font-medium ">Vat</label>
                                        <InputText id="payer-vat" v-model="payerForm.vat" placeholder="Vat"
                                            class="w-full mt-1 md:w-5/6" />
                                        <small class="error" v-if="paymentErrors.vat">{{ paymentErrors.vat
                                            }}</small>
                                    </div>

                                    <div class="flex flex-col mt-3">
                                        <label for="payer-contact" class="block text-sm font-medium ">Contact
                                            Person</label>
                                        <InputText id="payer-contact" v-model="payerForm.contact"
                                            placeholder="Contact Person" class="w-full mt-1 md:w-5/6" />
                                    </div>

                                    <div class="flex flex-col mt-3">
                                        <label for="payer-note" class="block text-sm font-medium ">Note</label>
                                        <Textarea rows="2" id="payer-note" v-model="payerForm.note" placeholder="Note"
                                            class="w-full mt-1 md:w-5/6" />
                                    </div> -->
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Section B -->
                <div class="flex flex-col md:flex-row gap-4">
                    <!-- Section C -->
                    <div class="flex-1  card-bg border rounded-xl border-gray-300 px-5 py-2 mt-2">
                        <div class="flex items-center gap-2 border-b-2 border-black w-full">
                            <h2 class="text-xl font-bold">Affiliate Information</h2>
                            <i class="pi pi-info-circle text-blue-500 cursor-help"
                                v-tooltip.bottom="'This information is used for voonix to pull data from the advertiser platform. We are using REV-CPA by default for now for all advertisers.'" />
                        </div>

                        <!--
                          Shared-login scope selector — only shown in edit mode when this
                          VoonixAdvertiser's SpaceAdvertiserLogin is referenced by ≥1 other
                          VoonixAdvertiser. Defaults to 'this' so admins don't accidentally
                          blast-mutate every sibling's credentials.
                        -->
                        <div v-if="props.mode === 'edit' && loginSharing.isShared"
                             class="mt-3 p-3 rounded-lg border border-amber-400 bg-amber-50 dark:bg-amber-950/30">
                            <div class="flex items-start gap-2">
                                <i class="pi pi-exclamation-triangle text-amber-600 mt-0.5" />
                                <div class="flex-1">
                                    <div class="text-sm font-semibold">
                                        This login is shared with {{ loginSharing.siblingCount }}
                                        other Voonix advertiser{{ loginSharing.siblingCount === 1 ? '' : 's' }}
                                    </div>
                                    <div class="text-xs text-gray-600 dark:text-gray-300 mt-1"
                                         v-if="loginSharing.siblings?.length">
                                        <span class="font-medium">Others:</span>
                                        {{ loginSharing.siblings.map(s => s.name).join(', ') }}
                                    </div>
                                    <div class="mt-2">
                                        <label class="block text-sm font-medium mb-1">
                                            Apply credential changes to:
                                        </label>
                                        <div class="flex flex-col md:flex-row gap-3">
                                            <label class="flex items-center gap-2 cursor-pointer">
                                                <RadioButton
                                                    v-model="loginUpdateScope"
                                                    inputId="scope-this"
                                                    value="this" />
                                                <span class="text-sm">
                                                    Only this advertiser
                                                    <span class="text-gray-500">(detach onto a separate login)</span>
                                                </span>
                                            </label>
                                            <label class="flex items-center gap-2 cursor-pointer">
                                                <RadioButton
                                                    v-model="loginUpdateScope"
                                                    inputId="scope-all"
                                                    value="all" />
                                                <span class="text-sm">
                                                    All {{ loginSharing.siblingCount + 1 }} advertisers sharing this login
                                                    <span class="text-red-600">(affects siblings)</span>
                                                </span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="flex flex-col md:flex-row gap-4">
                            <div class="mt-3 flex-1">
                                <label for="affiliate-system" class="block text-sm font-medium ">
                                    Affiliate System
                                </label>
                                <Select :loading="affSystemsloading" id="affiliateSystem"
                                    :invalid="errors.affiliateSystem !== null" v-model="form.login.affiliateSystem"
                                    :options="affiliateSystems" optionLabel="name"  filter
                                    aria-labelledby="basic" placeholder="Select a system" class="w-full mt-1" />
                                <small class="error" v-if="errors.affiliateSystem">{{ errors.affiliateSystem }}</small>
                            </div>

                            <div class="mt-3 flex-1">
                                <label for="affiliate-market" class="block text-sm font-medium ">
                                    Affiliate Market
                                </label>
                                <MultiSelect id="affiliateMarket" :invalid="errors.affiliateMarket !== null"
                                    v-model="form.affiliateMarket" :options="affiliateMarkets" optionLabel="name"
                                    optionValue="name" aria-labelledby="basic" placeholder="Select a Market"
                                    class="w-full mt-1" />
                                <small class="error" v-if="errors.affiliateMarket">{{ errors.affiliateMarket }}</small>
                            </div>

                            <div class="mt-3 flex-1">
                                <label for="affiliate-login-url" class="block text-sm font-medium ">
                                    Affiliate Login URL
                                </label>
                                <div class="flex gap-2 items-start">
                                    <InputText id="loginUrl" v-model="form.login.loginUrl" :invalid="errors.loginUrl !== null"
                                        placeholder="Affiliate Login URL" class="w-full mt-1" required />
                                    <Button icon="pi pi-external-link" text rounded v-tooltip.bottom="'Open login URL'"
                                        @click="openUrl(form.login.loginUrl)" :disabled="!form.login.loginUrl" class="mt-1"
                                        severity="secondary" />
                                </div>
                                <small class="error" v-if="errors.loginUrl">{{ errors.loginUrl }}</small>
                            </div>
                        </div>
                        <!-- Login Type Section -->
                        <div class="md:grid grid-cols-1 md:grid-cols-3 gap-x-8 xl:login-type-grid">
                            <div class="flex flex-col mt-3">
                                <label for="type" class="block text-sm font-medium "
                                    v-tooltip.top="'We are defaulting to REV share 100% for now'">
                                    Type
                                </label>
                                <Select disabled id="login_type" filter class="border rounded px-3 w-full mt-1 md:w-40"
                                    :invalid="errors.login_type !== null" v-model="form.type"
                                    :options="login_types" optionLabel="value" aria-labelledby="basic"
                                    optionValue="value" placeholder="Type" />
                                <small class="text-red-500" v-if="errors.login_type">{{ errors.login_type }}</small>
                            </div>
                            <div class="flex flex-col mt-3" v-if="form.type.includes('REV')">
                                <label for="user" class="block text-sm font-medium ">Revshare</label>
                                <div class="flex items-center">
                                    <InputText disabled id="rev" v-model="form.rev" :invalid="errors.rev !== null"
                                        placeholder="REV" class="border rounded px-3 py-2   mt-1 w-full md:w-40 "
                                        required />
                                    <span class="ml-2">%</span>
                                </div>
                                <small class="error" v-if="errors.rev">{{ errors.rev }}</small>
                            </div>
                            <div class="flex flex-col mt-3" v-if="form.type.includes('CPA')">
                                <label for="cpa" class="block text-sm font-medium ">CPA</label>
                                <InputText disabled id="cpa" v-model="form.cpa" :invalid="errors.cpa !== null"
                                    placeholder="CPA" class="border rounded px-3 py-2  mt-1 w-full md:w-40" required />
                                <small class="error" v-if="errors.cpa">{{ errors.cpa }}</small>
                            </div>
                            <div class="flex flex-col mt-3" v-if="form.type.includes('CPL')">
                                <label for="cpl" class="block text-sm font-medium ">CPL</label>
                                <InputText disabled  id="cpl" v-model="form.cpl" :invalid="errors.cpl !== null"
                                    placeholder="CPL" class="border rounded px-3 py-2 mt-1 w-full md:w-40" required />
                                <small class="error" v-if="errors.cpl">{{ errors.cpl }}</small>
                            </div>
                        </div>

                        <!-- Login Credentials Section -->
                        <div class="mt-2 md:grid grid-cols-1 xl:grid-cols-2 gap-4 xl:login-type-grid">
                            <div class="flex flex-col mt-3">
                                <label for="user" class="block text-sm font-medium ">Username</label>
                                <div class="flex items-center gap-2 w-full sm:w-4/5">
                                    <InputText id="username" v-model="form.login.username"
                                        :invalid="errors.login_username !== null" placeholder="Username"
                                        class="border rounded px-3 py-2 w-full mt-1" required />
                                    <Button v-if="mode === 'edit'" icon="pi pi-copy" severity="secondary"
                                        @click="copyToClipboard(form.login.username)"
                                        class="p-button-text p-button-sm mt-1" v-tooltip.top="'Copy username'" />
                                </div>
                                <small class="error" v-if="errors.login_username">{{ errors.login_username }}</small>
                            </div>
                            <div class="flex flex-col mt-3">
                                <label for="contact-email" class="block text-sm font-medium ">Password</label>
                                <div class="flex items-center gap-2 w-full sm:w-4/5">
                                    <InputText id="password" v-model="form.login.password"
                                        :invalid="errors.login_password !== null" placeholder="Password" required
                                        class="border rounded px-3 py-1.5 w-full mt-1" />
                                    <Button v-if="mode === 'edit'" icon="pi pi-copy" severity="secondary"
                                        @click="copyToClipboard(form.login.password)"
                                        class="p-button-text p-button-sm mt-1" v-tooltip.top="'Copy password'" />
                                </div>
                                <small class="text-red-500" v-if="errors.login_password">{{ errors.login_password
                                    }}</small>
                            </div>
                        </div>

                        <div class="mt-2 md:grid grid-cols-1 xl:grid-cols-2 gap-4 xl:login-type-grid">
                            <div class="flex flex-col mt-3">
                                <label for="key1" class="block text-sm font-medium">
                                    {{ keySpec('key1').label }}
                                    <span v-if="keySpec('key1').required" class="text-amber-400 ml-1">(required)</span>
                                </label>
                                <InputText id="key1" v-model="form.login.key1"
                                    :placeholder="keySpec('key1').label"
                                    class="border rounded px-3 py-2 w-full mt-1 sm:w-4/5" />
                                <small v-if="keySpec('key1').hint" class="text-xs text-gray-400 mt-1 sm:w-4/5">
                                    {{ keySpec('key1').hint }}
                                </small>
                                <small v-if="keyWarning('key1')"
                                       class="text-xs text-amber-400 mt-1 sm:w-4/5">
                                    Will be added but flagged as needing action until this is set.
                                    <a v-if="helpHref"
                                       :href="helpHref" target="_blank" rel="noopener noreferrer"
                                       class="underline hover:text-amber-300">See docs.</a>
                                </small>
                            </div>
                            <div class="flex flex-col mt-3">
                                <label for="key2" class="block text-sm font-medium">
                                    {{ keySpec('key2').label }}
                                    <span v-if="keySpec('key2').required" class="text-amber-400 ml-1">(required)</span>
                                </label>
                                <InputText id="key2" v-model="form.login.key2"
                                    :placeholder="keySpec('key2').label"
                                    class="border rounded px-3 py-1.5 w-full mt-1 sm:w-4/5 " />
                                <small v-if="keySpec('key2').hint" class="text-xs text-gray-400 mt-1 sm:w-4/5">
                                    {{ keySpec('key2').hint }}
                                </small>
                                <small v-if="keyWarning('key2')"
                                       class="text-xs text-amber-400 mt-1 sm:w-4/5">
                                    Will be added but flagged as needing action until this is set.
                                    <a v-if="helpHref"
                                       :href="helpHref" target="_blank" rel="noopener noreferrer"
                                       class="underline hover:text-amber-300">See docs.</a>
                                </small>
                            </div>
                        </div>

                        <!-- Affiliate Settings Section -->
                        <div class="mt-6">
                            <div class="flex items-center justify-between">
                                <div class="flex items-center gap-4">
                                    <label for="enabled-expected-income" class="text-sm font-medium">
                                        Expected Income Enabled
                                    </label>
                                    <ToggleSwitch 
                                        id="enabled-expected-income" 
                                        v-model="form.enabledExpectedIncome"
                                        v-tooltip.bottom="form.enabledExpectedIncome ? 'Expected income is showing' : 'Expected income is hidden'"
                                        :class="{ 'enabled-toggle': form.enabledExpectedIncome, 'disabled-toggle': !form.enabledExpectedIncome }"
                                    >
                                        <template #handle="{ checked }">
                                            <i :class="['!text-xs pi', { 'pi-check': checked, 'pi-times': !checked }]" />
                                        </template>
                                    </ToggleSwitch>
                                </div>

                                <div class="flex items-center gap-4">
                                    <label for="voonix-advertiser-active" class="text-sm font-medium">
                                        Active
                                    </label>
                                    <ToggleSwitch 
                                        id="voonix-advertiser-active" 
                                        v-model="form.isActive"
                                        v-tooltip.bottom="form.isActive ? 'Advertiser is Active' : 'Advertiser is Inactive'"
                                        :class="{ 'enabled-toggle': form.isActive, 'disabled-toggle': !form.isActive }"
                                    >
                                        <template #handle="{ checked }">
                                            <i :class="['!text-xs pi', { 'pi-check': checked, 'pi-times': !checked }]" />
                                        </template>
                                    </ToggleSwitch>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="w-full flex mt-5 justify-center content-center">
            <Button class="md:w-1/5" :loading="isLoading" severity="primary"
                :label="mode === 'edit' ? 'Update Advertiser' : 'Add Advertiser'" @click="AddOrUpdate"
                />
        </div>

    </div>
</template>



<script setup>
import { ref, reactive, toRaw, onMounted, watch, computed } from 'vue';
import { useRouter } from 'vue-router';
import { getFieldsForSystem, getHelpSectionForSystem } from '@/utils/affiliate-system-fields';
import { useConfirm } from "primevue/useconfirm";
import { openUrl } from '@/utils/url';
import { currencies } from '@/utils/currencies.js';
import VoonixAdvertiserService from '@/services/VoonixAdvertiserService';
import { useToast } from 'primevue/usetoast';
import RadioButton from 'primevue/radiobutton';
import affSystemService from '@/services/AffSystemService';
import AdvertiserService from '@/services/AdvertiserService';
import { paymentMethods } from '@/utils/paymentMethods';

const confirm = useConfirm();
const emit = defineEmits(['brandAdded', 'brandUpdated']);

//errors
const errors = reactive({
    name: null,
    description: null,
    spaceAdvertiser: null,
    affiliateMarket: null,
    affiliateSystem: null,
    currency: null,
    loginUrl: null,
    login_type: null,
    rev: null,
    cpa: null,
    cpl: null,
    login_username: null,
    login_password: null,
});

const paymentErrors = reactive({
    paymentMethod: null,
    name: null,
    paymentProvider: null,
    country: null,
    city: null,
    address: null,
    vat: null,
});

//validators
const validateForm = () => {
    errors.name = form.name.length >= 3 ? null : 'Name must be at least 3 characters long.';
    errors.spaceAdvertiser = form.spaceAdvertiserId ? null : 'Please select a space advertiser.';
    // errors.description = form.description.length >= 3 ? null : 'Description must be at least 3 characters long.';
    errors.affiliateMarket = form.affiliateMarket.length !== 0 ? null : 'Please select an affiliate market.';
    errors.affiliateSystem = form.login.affiliateSystem ? null : 'Please select an affiliate system.';
    errors.currency = form.currency ? null : 'Please select a currency.';
    if (!form.login.loginUrl) {
        errors.loginUrl = 'Affiliate login URL is required.';
    }
    else errors.loginUrl = null

    errors.cpa = form.type.includes('CPA') && !form.cpa ? 'CPA is required' : null;
    errors.cpl = form.type.includes('CPL') && !form.cpl ? 'CPL is required' : null;
    errors.rev = form.type.includes('REV') && !form.rev ? 'Revshare is required' : null;
    errors.login_type = form.type.length >= 3 ? null : 'Login Type is required';
    errors.login_username = form.login.username ? null : 'Username is required';
    errors.login_password = form.login.password ? null : 'Login password is required.';
    return Object.values(errors).every(error => error === null);
};

const validatePaymentForm = () => {
    paymentErrors.paymentMethod = payerForm.paymentMethod ? null : 'Payment method is required';
    // paymentErrors.name = payerForm.name ? null : 'Name is required';
    // paymentErrors.paymentProvider = payerForm.paymentProvider ? null : 'Payment provider is required';
    // paymentErrors.country = payerForm.country ? null : 'Country is required';
    // paymentErrors.city = payerForm.city ? null : 'City is required';
    // paymentErrors.address = payerForm.address ? null : 'Address is required';
    // paymentErrors.vat = payerForm.vat ? null : 'Vat is required';

    console.log('paymentErrors', paymentErrors);
    return Object.values(paymentErrors).every(error => error === null);
};

function validateSpaceAdvertiserName() {
    nameError.value = newSpaceAdvertiserName.value.length >= 3 ? null : 'Name must be at least 3 characters long.';
    return nameError.value === null;
}

//loading states
const isLoading = ref(false);
const affSystemsloading = ref(true);
const isCreatingSpaceAdvertiser = ref(false);
const loadingSpaceAdvertisers = ref(false);

//props
const props = defineProps({
    mode: {
        type: String,
        default: 'create'
    },
    advertiser: {
        type: Object,
        default: null
    }
});

//options
const affiliateMarkets = ref([
    { name: 'iGaming', code: 'iGaming' },
    { name: 'E-commerce', code: 'E-commerce' },
    { name: 'Finance', code: 'Finance' },
]);

const login_types = ref([
    { label: 'Revshare', value: 'REV' },
    { label: 'CPA', value: 'CPA' },
    { label: 'CPL', value: 'CPL' },
    { label: 'Hybrid: Revshare + CPA', value: 'REV-CPA' },
    { label: 'Hybrid: Revshare + CPL', value: 'REV-CPL' },
    { label: 'Hybrid: CPA + CPL', value: 'CPA-CPL' },
]);

const paymentMethodOptions = ref(paymentMethods);

const paymentFrequencies = ref([
    { name: 'Monthly', code: 'monthly' },
    { name: 'Bi-Weekly', code: 'bi-weekly' }
]);

// Access store and router
const toast = useToast();


const newSpaceAdvertiserName = ref('');
const nameError = ref(null);


const openCreateNewSpaceAdvertiserPrompt = (event) => {
    newSpaceAdvertiserName.value =  form.name;
    confirm.require({
        target: event.currentTarget,
        group: 'headless',
        message: 'Save your current process?',
        accept: async () => {
            await createSpaceAdvertiser();
        },
        reject: () => {
        }
    });
}

async function createSpaceAdvertiser() {
    if (validateSpaceAdvertiserName()) {
        isCreatingSpaceAdvertiser.value = true;
        const newAdvertiser = {
            name: newSpaceAdvertiserName.value
        };

        try {
            const response = await AdvertiserService.add(newAdvertiser);
            console.log('Space Advertiser added successfully:', response.data);
            toast.add({
                severity: 'info',
                summary: 'Space Advertiser created',
                detail: `Name: ${newSpaceAdvertiserName.value}`,
                life: 3000
            });
            await fetchSpaceAdvertisers();
            form.spaceAdvertiserId = response.data._id;
        } catch (error) {
            console.error('Error adding advertiser:', error);
            toast.add({
                severity: 'error',
                summary: 'Rejected',
                detail: error.response?.data.message || 'Failed to create space advertiser',
                life: 3000
            });
        } finally {
            newSpaceAdvertiserName.value = '';
            isCreatingSpaceAdvertiser.value = false;
        }
    } else {
        console.log('Form is invalid. Please correct the errors.');
    }
}

const AddOrUpdate = async () => {
    if (props.mode === 'edit') {
        await updateVoonixAdvertiser();
    } else {
        await createVoonixAdvertiser();
    }
}

const updateVoonixAdvertiser = async () => {
    if (showPayerForm.value) {
        if (!validatePaymentForm()) {
            toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Missing fields in payment form',
                life: 6000
            });
            return;
        }
    }
    isLoading.value = true;
    // Only forward the scope when the login is actually shared — otherwise the
    // server short-circuits to mutate-in-place regardless and the flag is noise.
    const scopeToSend = loginSharing.value.isShared ? loginUpdateScope.value : undefined;
    try {
        await VoonixAdvertiserService.update(
            props.advertiser._id,
            form,
            showPayerForm.value ? payerForm : null,
            scopeToSend
        );
        toast.add({
            severity: 'warn',
            summary: 'Success',
            detail: 'Voonix advertiser updated successfully',
            life: 3000
        });
        emit('brandAdded');
    } catch (err) {
        console.error(err);
        const status = err.response?.status;
        const message = err.response?.data?.message || err.response?.data;
        if (status === 409) {
            toast.add({
                severity: 'warn',
                summary: 'Conflict — needs manual review',
                detail: message || 'A conflicting SpaceAdvertiserLogin was detected.',
                life: 10000
            });
        } else {
            toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to update voonix advertiser: ' + (message || 'unknown error'),
                life: 6000
            });
        }
    }
    isLoading.value = false;
}

// --- CellXpert operator handoff -------------------------------------------
// A new CellXpert login is not usable until the operator whitelists our puller
// IP and exposes the qFTD / NGR columns. Neither is something we can do from
// our side, and both are silent failures - the login saves fine and simply
// returns no data - so the admin is shown the exact message to send and has
// to acknowledge it. Deliberately NOT a toast: a toast disappears on its own
// and this step gets forgotten.
const CELLXPERT_PULLER_IP = '95.217.229.246';
const showCellxpertHandoff = ref(false);
const cellxpertHandoffFor = ref('');

const cellxpertMessage = computed(() =>
`Hi, can you please whitelist this IP: ${CELLXPERT_PULLER_IP}

Also, can we get the qFTD and NGR columns exposed for us?

Thanks!`);

const isCellxpert = computed(() =>
    selectedSystemName.value.toLowerCase().replace(/\s+/g, '') === 'cellxpert');

function confirmCellxpertHandoff() {
    showCellxpertHandoff.value = false;
    // Emitted only now, so the parent closes the form after the admin has
    // acknowledged rather than whipping the message away on save.
    emit('brandAdded');
}

const createVoonixAdvertiser = async () => {
    isLoading.value = true;

    if (validateForm()) {
        try {
            console.log('form', form);
            await VoonixAdvertiserService.add(form, showPayerForm.value ? payerForm : null);
            if (isCellxpert.value) {
                cellxpertHandoffFor.value = form.login?.username || form.name || 'this login';
                showCellxpertHandoff.value = true;
            } else {
                emit('brandAdded');
            }
        } catch (err) {
            toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to add voonix advertiser: ' + err.response.data,
                life: 6000
            });
        }
    }
    isLoading.value = false;
};

const copyToClipboard = async (text) => {
    try {
        await navigator.clipboard.writeText(text);
        toast.add({
            severity: 'success',
            summary: 'Copied',
            detail: 'Text copied to clipboard',
            life: 2000
        });
    } catch (err) {
        toast.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to copy text',
            life: 3000
        });
    }
};

const affiliateSystems = ref([]);
async function fetchSystems() {
    try {
        await affSystemService.getAll().then((response) => {
            affiliateSystems.value = response.data
            affSystemsloading.value = false;
        });
    } catch (err) {
        console.error(err);
    } finally {
    }
}

const spaceAdvertisers = ref([]);
const fetchSpaceAdvertisers = async () => {
    try {
        loadingSpaceAdvertisers.value = true;
        const response = await AdvertiserService.getAllNames();
        spaceAdvertisers.value = response.data;
        console.log('Space Advertisers loaded:', spaceAdvertisers.value);
    } catch (error) {
        console.error('Error fetching advertisers:', error);
        toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to load advertisers' });
    } finally {
        loadingSpaceAdvertisers.value = false;
    }
};

const initForm = () => {
    // If in edit mode, fetch space advertisers
    if (props.mode === 'edit' && props.advertiser) {
        fetchSpaceAdvertisers();
    }

    return {
        name: props.advertiser?.name || '',
        description: props.advertiser?.description || '',
        // Fix: Handle markets properly based on incoming data format
        affiliateMarket: props.advertiser?.markets
            ? (Array.isArray(props.advertiser.markets)
                ? props.advertiser.markets
                : [props.advertiser.markets])
            : ['iGaming'],
        enabledExpectedIncome: props.advertiser?.enabledExpectedIncome || false,
        isActive: props.advertiser?.isActive || true,
        currency: props.advertiser?.currency || 'EUR',
        spaceAdvertiserId: props.advertiser?.advertiserId || null,
        payerId: props.advertiser?.payerId?._id || null,
        type: props.advertiser?.type || 'REV-CPA',
        rev: props.advertiser?.rev || '100',
        cpa: props.advertiser?.cpa || '0',
        cpl: props.advertiser?.cpl || null,
        login: {
            loginUrl: props.advertiser?.login?.loginUrl || '',
            username: props.advertiser?.login?.username || '',
            password: props.advertiser?.login?.password || '',
            key1: props.advertiser?.login?.key1 || '',
            key2: props.advertiser?.login?.key2 || '',
            affiliateSystem: props.advertiser?.login?.affiliateSystem || null,

        }
    };
};

const showPayerForm = ref(false);
const initPayerForm = (payer) => {
    if (props.advertiser?.payerId) {
        showPayerForm.value = true;
    }
    return {
        name: payer?.name || null,
        paymentMethod: payer?.paymentMethod || null,
        threshold: payer?.threshold || 0,
        unstructuredPayerDetails: payer?.unstructuredPayerDetails,
        paymentProvider: payer?.paymentProvider || '',
        paymentFrequency: payer?.paymentFrequency || 'monthly',
        country: payer?.country || '',
        city: payer?.city || '',
        address: payer?.address || '',
        zip: payer?.zip || '',
        vat: payer?.vat || '',
        contact: payer?.contact || '',
        note: payer?.note || ''
    };

};

const hidePayerForm = () => {
    showPayerForm.value = false;
    // Reset form
    payerForm.value = {
        paymentMethod: null,
        threshold: null,
        name: '',
        paymentProvider: '',
        country: '',
        city: '',
        address: '',
        zip: '',
        vat: '',
        contact: '',
        note: ''
    };
};

const form = reactive(initForm());
const payerForm = reactive(initPayerForm(props.advertiser?.payerId));

// --- per-affiliate-system Key 1 / Key 2 label + hint + soft-warning ---------
// Reflects what's configured in utils/affiliate-system-fields.js. If the
// selected system marks a key as required, the label upgrades from "Key 1"
// to the system's field name (e.g. "Statistic token") + a "(required)" tag,
// the hint sits below, and if the input is empty an amber note tells the
// admin the row will be flagged after save. Non-blocking — save still works.
const router = useRouter();
const selectedSystemName = computed(() => {
    const s = form.login?.affiliateSystem;
    return typeof s === 'string' ? s : (s?.name || '');
});
const systemFields = computed(() => getFieldsForSystem(selectedSystemName.value));
function keySpec(field) {
    const cfg = systemFields.value?.[field];
    return {
        label: cfg?.label || (field === 'key1' ? 'Key 1' : 'Key 2'),
        hint: cfg?.hint || '',
        required: !!cfg?.required,
    };
}
function keyWarning(field) {
    return keySpec(field).required && !(form.login?.[field] || '').toString().trim();
}
const helpHref = computed(() => {
    const slug = getHelpSectionForSystem(selectedSystemName.value);
    return slug ? router.resolve({ name: 'Help', query: { section: slug } }).href : null;
});

// --- shared SpaceAdvertiserLogin: scope-aware update ------------------------
// In edit mode, probe the server to find out if the SpaceAdvertiserLogin is
// shared with other VoonixAdvertisers. If it is, show a scope selector so the
// admin chooses whether changes apply only here ('this') or to every sibling
// under the same login ('all'). Default to 'this' — safer than blast-mutating.
const loginSharing = ref({ isShared: false, siblingCount: 0, siblings: [] });
const loginUpdateScope = ref('this');
const isLoadingLoginSharing = ref(false);

const fetchLoginSharing = async () => {
    if (props.mode !== 'edit' || !props.advertiser?._id) return;
    isLoadingLoginSharing.value = true;
    try {
        const { data } = await VoonixAdvertiserService.getLoginSharing(props.advertiser._id);
        loginSharing.value = data;
    } catch (err) {
        console.error('Failed to probe login-sharing state', err);
    }
    isLoadingLoginSharing.value = false;
};

onMounted(async () => {
    await Promise.all([
        fetchSystems(),
        fetchSpaceAdvertisers(),
        fetchLoginSharing()
    ]);
});
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.error {
    color: red;
    font-size: 0.85rem;
    margin-top: 0.5rem;
}

.p-field {
    margin-bottom: 1em;
}

.login-type-grid {
    display: grid;
    grid-template-columns: 25% 25% 45%;
    gap: 1rem;
}

.p-accordionheader {
    padding: 0.9rem;
}

/* Preformatted so the operator message keeps its line breaks exactly as it
   will be pasted. */
.cx-message {
    margin: 0;
    padding: 0.75rem 0.9rem;
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid var(--p-content-border-color, #3f3f46);
    font-size: 0.85rem;
    line-height: 1.5;
    white-space: pre-wrap;
    word-break: break-word;
}
</style>